import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowLeft } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { Avatar } from '../../components/common/Avatar';
import { useTranslation } from '../../hooks/useTranslation';

interface CreateConversationProps {
  onConversationCreated: () => void;
}

interface User {
  id: string;
  username: string;
  avatar_url?: string;
}

export const CreateConversation = ({ onConversationCreated }: CreateConversationProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, getPathForLang } = useTranslation();

  const searchUsers = async (term: string) => {
    if (term.length < 3) {
      setUsers([]);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, user_id, username, avatar_url')
        .ilike('username', `%${term}%`)
        .neq('user_id', user?.id)
        .limit(10);

      if (error) throw error;
      
      // Transformer les données pour s'assurer que l'id est bien le user_id
      const transformedData = data?.map(profile => ({
        id: profile.user_id,
        username: profile.username,
        avatar_url: profile.avatar_url
      })) || [];
      
      setUsers(transformedData);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const startConversation = async (otherUser: User) => {
    if (!user || !user.id) {
      console.error('Current user is not defined');
      return;
    }
    
    try {
      console.log('Starting conversation with user:', otherUser);
      
      // Vérifier si une conversation existe déjà
      const { data: existingConversations, error: conversationsError } = await supabase
        .from('conversation_participants')
        .select(`
          conversation_id,
          conversations!inner(id)
        `)
        .eq('user_id', user.id);

      if (conversationsError) throw conversationsError;
      console.log('Existing conversations for current user:', existingConversations);

      if (existingConversations && existingConversations.length > 0) {
        // Parmi ces conversations, trouver celle qui contient l'autre utilisateur
        const { data: existingConversation, error: findError } = await supabase
          .from('conversation_participants')
          .select('conversation_id')
          .eq('user_id', otherUser.id)
          .in('conversation_id', existingConversations.map(c => c.conversation_id))
          .maybeSingle();

        if (findError && findError.code !== 'PGRST116') throw findError;
        console.log('Existing conversation with other user:', existingConversation);

        if (existingConversation) {
          // Si une conversation existe, rediriger vers celle-ci
          navigate(getPathForLang(`/messages/${existingConversation.conversation_id}`));
          onConversationCreated();
          return;
        }
      }

      // Créer une nouvelle conversation
      const { data: conversation, error: conversationError } = await supabase
        .from('conversations')
        .insert({
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (conversationError) throw conversationError;
      console.log('New conversation created:', conversation);

      // Ajouter les participants
      const participants = [
        {
          conversation_id: conversation.id,
          user_id: user.id
        },
        {
          conversation_id: conversation.id,
          user_id: otherUser.id
        }
      ];
      
      console.log('Adding participants:', participants);

      const { data: participantsData, error: participantsError } = await supabase
        .from('conversation_participants')
        .insert(participants)
        .select();

      if (participantsError) throw participantsError;
      console.log('Participants added:', participantsData);

      // Notifier que la conversation a été créée
      onConversationCreated();

      // Rediriger vers la conversation
      navigate(getPathForLang(`/messages/${conversation.id}`));
    } catch (error) {
      console.error('Error starting conversation:', error);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* En-tête */}
      <div className="p-4 border-b border-gray-200 flex items-center gap-3">
        <button 
          onClick={() => navigate(getPathForLang('/messages'))}
          className="text-gray-600 hover:text-gray-900"
          aria-label={t('messages.conversation.back_to_list_aria')}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="text-lg font-semibold">{t('messages.new_conversation.title')}</h2>
      </div>

      {/* Barre de recherche */}
      <div className="p-4 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder={t('messages.new_conversation.search_placeholder')}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              searchUsers(e.target.value);
            }}
            className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Liste des résultats */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4">
            <div className="animate-pulse space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-200 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : searchTerm.length < 3 ? (
          <div className="p-8 text-center text-gray-500">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p>{t('messages.new_conversation.search_min_chars')}</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>{t('messages.new_conversation.no_user_found')}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {users.map((user) => (
              <button
                key={user.id}
                onClick={() => startConversation(user)}
                className="w-full px-4 py-3 hover:bg-gray-50 flex items-center gap-3 transition-colors"
              >
                <Avatar 
                  url={user.avatar_url} 
                  username={user.username}
                  size="md"
                />
                <div className="text-left">
                  <h3 className="font-medium text-gray-900">{user.username}</h3>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}; 